import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Upload, Download, FileText, Image as ImageIcon, Eye, Trash2, Search, X, Plus, GripVertical, FolderPlus, ChevronRight, ChevronDown, ChevronLeft } from "lucide-react";
import {
    DndContext, PointerSensor, useSensor, useSensors,
    useDraggable, useDroppable, closestCenter,
} from "@dnd-kit/core";
import { api, API } from "../../lib/api";
import { usePageMeta } from "../../components/Layout";
import { toast } from "sonner";
import {
    useFileCategories,
    migrateCategoryLegacy,
} from "../../contexts/FileCategoriesContext";

const WS_NAMES = { 1:"Initial Enquiry + Brief Development", 2:"Project Feasibility", 3:"Concept Design", 4:"Developed + Technical Design", 5:"Pre-Construction", 6:"Construction", 7:"Post-Construction" };
const WS_LIST = [1, 2, 3, 4, 5, 6, 7];
import {
    SOURCE_ORIGIN_LABELS, sourceOriginLabel,
    BATCH_DOWNLOAD_MAX_BYTES, fmt, bytes, TypeIcon, fileTypeDisplay,
} from "../../lib/filesCommon";
const WS_PILLS = [1, 2, 3, 4, 5, 6, 7, "unassigned"];
const SORT_OPTIONS = [
    { key: "updated_at",       label: "Updated (newest first)", dir: "desc" },
    { key: "updated_at",       label: "Updated (oldest first)", dir: "asc"  },
    { key: "name",             label: "Filename A–Z",           dir: "asc"  },
    { key: "name",             label: "Filename Z–A",           dir: "desc" },
    { key: "document_number",  label: "Document Number",        dir: "asc"  },
    { key: "category",         label: "Category",               dir: "asc"  },
];

// Phase C — canonical source_origin → display label map (mirrors backend
// stage5_file_categories.SOURCE_ORIGINS).  Keep in sync when adding keys.
// Phase G — 500MB cap, mirrored from backend BATCH_DOWNLOAD_MAX_BYTES.
// (constants + helpers lifted to /src/lib/filesCommon.js in Dispatch 5
//  Phase F — see import block at top of file.)

function Pill({ tone, children }) {
    const map = {
        current: "bg-charcoal text-white",
        superseded: "bg-[#EBE8E0] text-[#666]",
        withdrawn: "bg-[#EDE3E1] text-[#7A5148]",
        removed: "bg-[#EDE3E1] text-[#7A5148]",
        draft: "bg-[#F5EFE7] text-[#7A6A50]",
        published: "bg-[#E8F0E7] text-[#3B5B37]",
        issued: "bg-[#DDE7F3] text-[#2E4A78]",
        default: "bg-white border border-[#E5E2DC] text-charcoal",
    };
    return <span className={`px-2 py-0.5 rounded-full text-[11px] font-manrope font-semibold uppercase tracking-wide ${map[tone]||map.default}`}>{children}</span>;
}
function versionStatePill(s){
    const t = { "Current":"current", "Superseded":"superseded", "Withdrawn":"withdrawn", "Removed by Client":"removed" }[s] || "default";
    return <Pill tone={t}>{s || "—"}</Pill>;
}
function pubStatePill(s){
    const t = { "Internal Draft":"draft", "Published":"published", "Formally Issued":"issued" }[s] || "default";
    return <Pill tone={t}>{s || "—"}</Pill>;
}

// Phase I — Filetype display lifted to filesCommon (see import).

// Persisted per-user expand/collapse state.
function useExpandState(storageKey) {
    const [state, setState] = useState(() => {
        try {
            const raw = window.localStorage.getItem(storageKey);
            return raw ? JSON.parse(raw) : {};
        } catch { return {}; }
    });
    useEffect(() => {
        try { window.localStorage.setItem(storageKey, JSON.stringify(state)); }
        catch {}
    }, [storageKey, state]);
    const isCollapsed = (key) => Boolean(state[key]);
    const toggle = (key) => setState((prev) => ({ ...prev, [key]: !prev[key] }));
    return { isCollapsed, toggle };
}

// =====================================================================
// Project Files — admin list + grouped view + custom groups + DnD + modal
// =====================================================================

export function ProjectFiles() {
    const { id, fileId } = useParams();
    const navigate = useNavigate();
    const { categories, byKey, adminSelectable } = useFileCategories();
    const [items, setItems] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Dispatch 4 — multi-file upload batch.
    const [uploadOpen, setUploadOpen] = useState(() => {
        try { return localStorage.getItem(
                `project_files_upload_open_${id}`) === "1"; }
        catch { return false; }
    });
    useEffect(() => {
        try { localStorage.setItem(
                `project_files_upload_open_${id}`,
                uploadOpen ? "1" : "0"); } catch { /* ignore */ }
    }, [id, uploadOpen]);
    const [shared, setShared] = useState({
        category: "", originating_workstage: 1,
        group_id: "", client_visible: false,
    });
    const [pending, setPending] = useState([]);  // see UploadPanel
    const [uploading, setUploading] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [searchQ, setSearchQ] = useState("");
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [wsFilter, setWsFilter] = useState(null);
    const [groupByWs, setGroupByWs] = useState(true);
    const [sortKey, setSortKey] = useState("updated_at");
    const [sortDir, setSortDir] = useState("desc");
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    // Phase H — per-WS new-group input state (keyed by workstage number).
    const [newGroupWs, setNewGroupWs] = useState(null);
    const [newGroupName, setNewGroupName] = useState("");
    const [renamingGid, setRenamingGid] = useState(null);
    const [renameInput, setRenameInput] = useState("");
    const [groupToDelete, setGroupToDelete] = useState(null);
    // Phase G — multi-select state: Map<file_id, {size, workstage}>.
    const [selected, setSelected] = useState(() => new Map());
    const [downloadingBatch, setDownloadingBatch] = useState(false);
    // Phase E — collapsible WS + sub-group state, persisted per-user.
    const expand = useExpandState(`project_files_group_state_${id}`);
    usePageMeta({ title: "Files · Project | Studio G4 Admin" });

    useEffect(() => {
        const t = setTimeout(() => setSearchQ(searchInput.trim()), 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    const categoryPills = useMemo(() =>
        (categories || []).map((c) => ({ key: c.key, label: c.label })),
    [categories]);

    const refresh = async () => {
        setLoading(true); setError(null);
        try {
            const params = new URLSearchParams({ current_only: "false" });
            if (searchQ) params.set("q", searchQ);
            if (categoryFilter) params.set("category", categoryFilter);
            if (wsFilter && wsFilter !== "unassigned") {
                params.set("originating_workstage", String(wsFilter));
            }
            const [r1, r2] = await Promise.all([
                api.get(`/admin/projects/${id}/files?${params.toString()}`),
                api.get(`/admin/projects/${id}/file-groups`),
            ]);
            setItems(r1.data?.items || []);
            setGroups(r2.data?.items || []);
        } catch { setError("Could not load files"); }
        finally { setLoading(false); }
    };
    useEffect(() => { refresh(); /* eslint-disable-next-line */ },
        [id, searchQ, categoryFilter, wsFilter]);

    const BATCH_MAX_FILES = 20;
    const BATCH_MAX_BYTES = 500 * 1024 * 1024;

    const addFiles = (incoming) => {
        if (!incoming || !incoming.length) return;
        const existing = pending.filter((p) => p.status !== "done");
        const existingBytes = existing.reduce((s, p) => s + p.file.size, 0);
        let bytes = existingBytes;
        const toAdd = [];
        for (const f of Array.from(incoming)) {
            if (f.size > BATCH_MAX_BYTES) {
                toast.error(
                    `"${f.name}" exceeds the 500MB per-batch cap`);
                continue;
            }
            if (existing.length + toAdd.length >= BATCH_MAX_FILES) {
                toast.error(
                    `Batch cap of ${BATCH_MAX_FILES} files reached`);
                break;
            }
            if (bytes + f.size > BATCH_MAX_BYTES) {
                toast.error(
                    "Selection exceeds the 500MB per-batch cap");
                break;
            }
            bytes += f.size;
            toAdd.push({
                tempId: `t-${Date.now()}-${Math.random().toString(36).slice(2,8)}`,
                file: f, status: "queued", pct: 0, error: null,
            });
        }
        if (toAdd.length) {
            setPending((prev) => [...prev, ...toAdd]);
        }
    };

    const removePending = (tempId) => {
        setPending((prev) => prev.filter((p) => p.tempId !== tempId));
    };

    const submitBatch = async () => {
        if (!shared.category) {
            toast.error("Select a category for this batch"); return;
        }
        const queue = pending.filter((p) => p.status !== "done");
        if (!queue.length) return;
        setUploading(true);
        const results = await Promise.all(queue.map(async (p) => {
            setPending((prev) => prev.map((x) =>
                x.tempId === p.tempId
                ? { ...x, status: "uploading", pct: 0, error: null }
                : x));
            try {
                const fd = new FormData();
                fd.append("name", p.file.name);
                fd.append("originating_workstage",
                            String(shared.originating_workstage));
                fd.append("category", shared.category);
                fd.append("client_visible",
                            String(shared.client_visible));
                fd.append("create_op_id",
                            `up-${Date.now()}-${p.tempId}`);
                fd.append("upload", p.file);
                const res = await api.post(
                    `/admin/projects/${id}/files`, fd, {
                        onUploadProgress: (ev) => {
                            if (!ev.total) return;
                            const pct = Math.round(
                                (ev.loaded / ev.total) * 100);
                            setPending((prev) => prev.map((x) =>
                                x.tempId === p.tempId
                                ? { ...x, pct } : x));
                        },
                    });
                // Assign to group if the batch picked one.
                const createdFid = res.data?.file?.id;
                if (createdFid && shared.group_id) {
                    try {
                        await api.patch(
                            `/admin/projects/${id}/files/${createdFid}`,
                            { group_id: shared.group_id });
                    } catch (gerr) {
                        // Group assignment is best-effort; surface as
                        // a soft warning but keep the upload as a success.
                        toast.error(
                            `"${p.file.name}" uploaded but group ` +
                            "assignment failed");
                    }
                }
                setPending((prev) => prev.map((x) =>
                    x.tempId === p.tempId
                    ? { ...x, status: "done", pct: 100 } : x));
                return { ok: true };
            } catch (err) {
                const reason = err?.response?.data?.detail?.reason
                    || err?.response?.data?.detail
                    || "Upload failed";
                setPending((prev) => prev.map((x) =>
                    x.tempId === p.tempId
                    ? { ...x, status: "failed",
                         error: typeof reason === "string"
                             ? reason : JSON.stringify(reason) }
                    : x));
                return { ok: false, name: p.file.name,
                         reason: typeof reason === "string"
                             ? reason : JSON.stringify(reason) };
            }
        }));
        setUploading(false);
        const okN = results.filter((r) => r.ok).length;
        const failN = results.length - okN;
        if (okN && !failN) {
            toast.success(
                `${okN} file${okN === 1 ? "" : "s"} uploaded`);
        } else if (okN && failN) {
            toast.message(
                `${okN} uploaded, ${failN} failed — see the batch panel`);
        } else if (failN) {
            toast.error(`All ${failN} uploads failed`);
        }
        // Prune successful rows; failed stay so admin can retry/remove.
        setPending((prev) => prev.filter((p) => p.status !== "done"));
        await refresh();
    };

    const resetSharedAfterBatch = () => {
        // Called on successful "clear complete" or manual reset.
        setShared({ category: "", originating_workstage: 1,
                      group_id: "", client_visible: false });
    };

    const hydratedItems = useMemo(() => items.map((entry) => ({
        ...entry,
        file: {
            ...entry.file,
            category: migrateCategoryLegacy(entry.file.category),
        },
    })), [items]);

    const filteredItems = useMemo(() => hydratedItems.filter(({ file }) => {
        if (wsFilter === "unassigned" && file.originating_workstage) return false;
        return true;
    }), [hydratedItems, wsFilter]);

    const sortedItems = useMemo(() => {
        const arr = [...filteredItems];
        const getKey = (entry) => {
            const f = entry.file;
            const cur = (entry.versions || []).find((v) => v.version_state === "Current")
                        || entry.versions?.[entry.versions.length - 1];
            if (sortKey === "name") return (f.name || "").toLowerCase();
            if (sortKey === "document_number") return (cur?.document_number || "").toLowerCase();
            if (sortKey === "category") return byKey[f.category]?.label || f.category || "";
            return f.updated_at || "";
        };
        arr.sort((a, b) => {
            const ka = getKey(a); const kb = getKey(b);
            if (ka < kb) return sortDir === "asc" ? -1 : 1;
            if (ka > kb) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
        return arr;
    }, [filteredItems, sortKey, sortDir, byKey]);

    // Phase E — hierarchical structure.  Groups are now children of a
    // workstage; render inside their WS section after ungrouped rows.
    //
    // Shape:
    //   [{ ws, ungrouped: [...], subGroups: [{ group, rows: [...] }] }]
    // Only workstages with at least one row (or declared group) render.
    const groupedByWs = useMemo(() => {
        const groupsByWs = new Map();
        for (const g of groups) {
            if (!groupsByWs.has(g.workstage)) groupsByWs.set(g.workstage, []);
            groupsByWs.get(g.workstage).push(g);
        }
        const rowsByWs = new Map();
        const rowsByGid = new Map();
        for (const entry of sortedItems) {
            if (entry.file.group_id) {
                if (!rowsByGid.has(entry.file.group_id)) rowsByGid.set(entry.file.group_id, []);
                rowsByGid.get(entry.file.group_id).push(entry);
            } else {
                const ws = entry.file.originating_workstage || "unassigned";
                if (!rowsByWs.has(ws)) rowsByWs.set(ws, []);
                rowsByWs.get(ws).push(entry);
            }
        }
        for (const list of rowsByGid.values()) {
            list.sort((a, b) => (a.file.group_position ?? 0) - (b.file.group_position ?? 0));
        }
        // Emit every WS in the fixed set (even when empty) so admins
        // see all 7 placeholder sections — matches pre-Dispatch-3
        // behaviour.  When a `wsFilter` is active, restrict to just
        // that WS to avoid confusing stub sections alongside a filter.
        const wsOrder = wsFilter
            ? [wsFilter]
            : [...WS_LIST, "unassigned"];
        const out = [];
        for (const ws of wsOrder) {
            const ungrouped = rowsByWs.get(ws) || [];
            const subGroups = (groupsByWs.get(ws) || []).map((g) =>
                ({ group: g, rows: rowsByGid.get(g.id) || [] }));
            out.push({ ws, ungrouped, subGroups });
        }
        return out;
    }, [sortedItems, groups, wsFilter]);

    const openDelete = (entry, ev) => {
        ev.stopPropagation();
        setDeleteTarget(entry);
    };
    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await api.delete(`/admin/projects/${id}/files/${deleteTarget.file.id}`);
            toast.success("File deleted");
            setDeleteTarget(null);
            await refresh();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 409) {
                toast.error(
                    (detail && detail.message)
                    || "Executed documents cannot be deleted — upload a new version instead.");
            } else {
                toast.error(
                    (typeof detail === "string" && detail)
                    || "Delete failed");
            }
        } finally {
            setDeleting(false);
        }
    };

    const patchFile = async (fid, patch, { optimistic } = {}) => {
        setItems((prev) => prev.map((entry) => {
            if (entry.file.id !== fid) return entry;
            const updated = { ...entry };
            if ("client_visible" in patch) {
                updated.versions = (entry.versions || []).map((v) =>
                    v.version_state === "Current"
                    ? { ...v, client_visible: patch.client_visible }
                    : v);
            }
            if ("originating_workstage" in patch) {
                updated.file = { ...entry.file,
                                  originating_workstage: patch.originating_workstage };
            }
            if ("category" in patch) {
                updated.file = { ...updated.file, category: patch.category };
            }
            if ("group_id" in patch) {
                updated.file = { ...updated.file, group_id: patch.group_id };
            }
            if ("group_position" in patch) {
                updated.file = { ...updated.file, group_position: patch.group_position };
            }
            if ("name" in patch) {
                updated.file = { ...updated.file, name: patch.name };
            }
            if ("document_number" in patch) {
                // `document_number` lives on the current version row.
                updated.versions = (entry.versions || []).map((v) =>
                    v.version_state === "Current"
                    ? { ...v, document_number: patch.document_number }
                    : v);
            }
            return updated;
        }));
        try {
            await api.patch(`/admin/projects/${id}/files/${fid}`, patch);
        } catch (err) {
            toast.error(err?.response?.data?.detail?.reason || "Save failed");
            await refresh();
        }
    };

    // ---- Phase F / H — groups CRUD ---------------------------------
    const openAddGroup = (ws) => {
        setNewGroupWs(ws); setNewGroupName("");
    };
    const submitNewGroup = async () => {
        const name = (newGroupName || "").trim();
        if (!name || newGroupWs == null) { setNewGroupWs(null); setNewGroupName(""); return; }
        try {
            const r = await api.post(`/admin/projects/${id}/file-groups`,
                                      { name, workstage: newGroupWs });
            setGroups((prev) => [...prev, r.data.group]);
            setNewGroupWs(null); setNewGroupName("");
            toast.success(`Group '${name}' created`);
        } catch (err) {
            const d = err?.response?.data?.detail;
            if (d?.error === "duplicate_group_name") {
                toast.error("Group name already exists in this workstage");
            } else {
                toast.error(d?.reason || "Could not create group");
            }
        }
    };
    const submitRename = async (gid) => {
        const name = (renameInput || "").trim();
        if (!name) { setRenamingGid(null); return; }
        try {
            const r = await api.patch(
                `/admin/projects/${id}/file-groups/${gid}`, { name });
            setGroups((prev) => prev.map((g) => g.id === gid ? r.data.group : g));
            setRenamingGid(null);
        } catch (err) {
            toast.error(err?.response?.data?.detail?.reason
                        || "Could not rename group");
        }
    };
    const confirmDeleteGroup = async () => {
        if (!groupToDelete) return;
        try {
            await api.delete(
                `/admin/projects/${id}/file-groups/${groupToDelete.id}`);
            setGroups((prev) => prev.filter((g) => g.id !== groupToDelete.id));
            toast.success(`Group '${groupToDelete.name}' deleted`);
            setGroupToDelete(null);
        } catch (err) {
            toast.error(err?.response?.data?.detail?.reason
                        || "Could not delete group");
        }
    };

    const resetFilters = () => {
        setSearchInput(""); setSearchQ("");
        setCategoryFilter(null); setWsFilter(null);
    };
    const filtersActive = Boolean(
        searchQ || categoryFilter || wsFilter);

    const clickHeader = (key) => {
        if (sortKey === key) {
            setSortDir((d) => d === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key); setSortDir("asc");
        }
    };

    // ---- Phase G — multi-select + batch zip download ----------------
    const toggleSelect = (entry) => {
        const f = entry.file;
        const cur = (entry.versions || []).find((v) => v.version_state === "Current")
                    || entry.versions?.[entry.versions.length - 1];
        const sz = cur?.size ?? f.size ?? 0;
        const ws = f.originating_workstage || "unassigned";
        setSelected((prev) => {
            const next = new Map(prev);
            if (next.has(f.id)) {
                next.delete(f.id);
                return next;
            }
            // Multi-select is scoped to a single workstage.
            if (next.size > 0) {
                const firstWs = [...next.values()][0].workstage;
                if (firstWs !== ws) {
                    toast.error("Multi-select is limited to a single workstage");
                    return prev;
                }
            }
            next.set(f.id, { size: sz, workstage: ws });
            return next;
        });
    };
    const toggleSelectAll = (rows) => {
        if (!rows.length) return;
        const ws = rows[0].file.originating_workstage || "unassigned";
        // If all already selected, deselect them.  Otherwise select all.
        const allSelected = rows.every((r) => selected.has(r.file.id));
        setSelected((prev) => {
            const next = new Map(prev);
            if (allSelected) {
                for (const r of rows) next.delete(r.file.id);
                return next;
            }
            // Refuse if another WS is already selected.
            if (next.size > 0) {
                const firstWs = [...next.values()][0].workstage;
                if (firstWs !== ws) {
                    toast.error("Multi-select is limited to a single workstage");
                    return prev;
                }
            }
            for (const r of rows) {
                const cur = (r.versions || []).find((v) => v.version_state === "Current")
                            || r.versions?.[r.versions.length - 1];
                next.set(r.file.id, {
                    size: cur?.size ?? r.file.size ?? 0,
                    workstage: ws,
                });
            }
            return next;
        });
    };
    const clearSelection = () => setSelected(new Map());
    const selectionTotalBytes = useMemo(() => {
        let n = 0;
        for (const v of selected.values()) n += (v.size || 0);
        return n;
    }, [selected]);
    const selectionExceedsLimit = selectionTotalBytes > BATCH_DOWNLOAD_MAX_BYTES;
    const downloadSelected = async () => {
        if (selected.size === 0 || selectionExceedsLimit) return;
        setDownloadingBatch(true);
        try {
            const res = await api.post(
                `/admin/projects/${id}/files/download-batch`,
                { file_ids: [...selected.keys()] },
                { responseType: "blob" });
            const disp = res.headers?.["content-disposition"] || "";
            const match = /filename="?([^"]+)"?/.exec(disp);
            const name = match ? match[1] : `project_${id}_files.zip`;
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url; a.download = name;
            document.body.appendChild(a); a.click();
            a.remove(); window.URL.revokeObjectURL(url);
            clearSelection();
        } catch (err) {
            const status = err?.response?.status;
            if (status === 413) {
                toast.error("Selection exceeds 500MB download limit");
            } else {
                toast.error(err?.response?.data?.detail?.reason
                            || "Download failed");
            }
        } finally {
            setDownloadingBatch(false);
        }
    };
    const downloadGroup = async (groupId) => {
        try {
            const res = await api.get(
                `/admin/projects/${id}/file-groups/${groupId}/download`,
                { responseType: "blob" });
            const disp = res.headers?.["content-disposition"] || "";
            const match = /filename="?([^"]+)"?/.exec(disp);
            const name = match ? match[1] : `project_${id}_group.zip`;
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url; a.download = name;
            document.body.appendChild(a); a.click();
            a.remove(); window.URL.revokeObjectURL(url);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 413) {
                toast.error("Group exceeds 500MB download limit");
            } else {
                toast.error(err?.response?.data?.detail?.reason
                            || "Download failed");
            }
        }
    };
    const downloadSingle = async (fid) => {
        // Single file: use the existing /admin/files/{fid}/download that
        // returns the current version (presigned for S3, stream for local).
        window.open(`${API}/admin/files/${fid}/download`, "_blank");
    };

    // ---- Phase G — drag-and-drop handler ---------------------------
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: { distance: 5 },
    }));
    const onDragEnd = useCallback((event) => {
        const { active, over } = event;
        if (!active || !over) return;
        const draggedId = String(active.id).replace(/^row-/, "");
        const overId = String(over.id);
        const draggedEntry = sortedItems.find((e) => e.file.id === draggedId);
        if (!draggedEntry) return;
        const file = draggedEntry.file;

        // Dropped on a workstage section (zone id: `ws-{n}` or `ws-unassigned`).
        if (overId.startsWith("ws-")) {
            const targetWs = overId.slice(3);
            const targetWsInt = targetWs === "unassigned" ? null : parseInt(targetWs, 10);
            if (file.originating_workstage !== targetWsInt) {
                toast.error("Cannot move a file between workstages.");
                return;
            }
            if (file.group_id) {
                patchFile(file.id, { group_id: null }, { optimistic: true });
            }
            return;
        }

        if (overId.startsWith("group-")) {
            const targetGid = overId.slice(6);
            if (file.group_id === targetGid) return;
            const target = groups.find((g) => g.id === targetGid);
            if (target && target.workstage !== file.originating_workstage) {
                toast.error("Cannot move a file between workstages.");
                return;
            }
            patchFile(file.id, { group_id: targetGid }, { optimistic: true });
            return;
        }

        if (overId.startsWith("row-")) {
            const overFid = overId.slice(4);
            const overEntry = sortedItems.find((e) => e.file.id === overFid);
            if (!overEntry) return;
            const overFile = overEntry.file;
            if (!overFile.group_id && !file.group_id) {
                if (overFile.originating_workstage !== file.originating_workstage) {
                    toast.error("Cannot move a file between workstages.");
                }
                return;
            }
            if (overFile.group_id) {
                const samePos = overFile.group_position ?? 0;
                const target = groups.find((g) => g.id === overFile.group_id);
                if (target && target.workstage !== file.originating_workstage) {
                    toast.error("Cannot move a file between workstages.");
                    return;
                }
                if (file.group_id === overFile.group_id) {
                    patchFile(file.id, { group_position: samePos + 1 },
                               { optimistic: true });
                } else {
                    patchFile(file.id, {
                        group_id: overFile.group_id,
                        group_position: samePos + 1,
                    }, { optimistic: true });
                }
                return;
            }
            if (file.group_id && !overFile.group_id) {
                if (overFile.originating_workstage !== file.originating_workstage) {
                    toast.error("Cannot move a file between workstages.");
                    return;
                }
                patchFile(file.id, { group_id: null }, { optimistic: true });
            }
        }
    }, [sortedItems, groups, patchFile]);

    const showModal = Boolean(fileId);
    const selectionCount = selected.size;

    const tableCtx = {
        id, byKey, adminSelectable,
        onPatch: patchFile, openDelete, navigate,
        onHeaderClick: clickHeader, sortKey, sortDir,
        selected, onToggleSelect: toggleSelect,
        onToggleSelectAll: toggleSelectAll,
        onDownloadSingle: downloadSingle,
    };

    return (
        <div className="flex flex-col gap-4" data-testid="files-page">
            <button onClick={()=>navigate(`/admin/projects/${id}`)} data-testid="files-back-to-workspace" className="self-start inline-flex items-center gap-1.5 text-[13px] text-[#666] hover:text-charcoal">
                <ArrowLeft className="w-4 h-4" /> Back to project workspace
            </button>
            <h1 className="font-manrope text-2xl font-semibold text-charcoal">Project Files</h1>

            <UploadPanel
                open={uploadOpen} setOpen={setUploadOpen}
                pending={pending} setPending={setPending}
                shared={shared} setShared={setShared}
                groups={groups} adminSelectable={adminSelectable}
                projectId={id} uploading={uploading}
                addFiles={addFiles} removePending={removePending}
                submitBatch={submitBatch}
                onGroupCreated={async (g) => {
                    // Refresh groups list + auto-select the new group.
                    try {
                        const r = await api.get(
                            `/admin/projects/${id}/file-groups`);
                        setGroups(r.data?.items || []);
                        setShared((s) => ({ ...s, group_id: g.id }));
                    } catch { /* ignore */ }
                }}
                formatBytes={bytes}
                maxFiles={BATCH_MAX_FILES}
                maxBytes={BATCH_MAX_BYTES} />

            {/* Toolbar */}
            <div className="bg-white border border-[#E5E2DC] rounded-lg p-3 flex flex-col gap-3" data-testid="files-toolbar">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative flex-1 min-w-[220px] max-w-md">
                        <Search className="w-4 h-4 text-[#999] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <input value={searchInput} onChange={(e)=>setSearchInput(e.target.value)}
                               placeholder="Search filename, document number…"
                               data-testid="files-search"
                               className="w-full border border-[#E5E2DC] rounded-md pl-9 pr-3 py-2 text-[13px] outline-none focus:border-charcoal" />
                    </div>
                    <select data-testid="files-toolbar-sort" value={`${sortKey}:${sortDir}`}
                            onChange={(e)=>{ const [k,d]=e.target.value.split(":"); setSortKey(k); setSortDir(d); }}
                            className="border border-[#E5E2DC] rounded-md px-3 py-2 text-[13px]">
                        {SORT_OPTIONS.map((o) => (
                            <option key={`${o.key}:${o.dir}`} value={`${o.key}:${o.dir}`}>{o.label}</option>
                        ))}
                    </select>
                    <label className="inline-flex items-center gap-2 text-[13px] text-[#666]">
                        <input type="checkbox" checked={groupByWs} onChange={(e)=>setGroupByWs(e.target.checked)}
                               data-testid="files-toolbar-group-by-ws" />
                        Group by workstage
                    </label>
                    {filtersActive && (
                        <button type="button" onClick={resetFilters}
                                data-testid="files-toolbar-reset"
                                className="inline-flex items-center gap-1 text-[12px] text-[#666] hover:text-charcoal">
                            <X className="w-3 h-3" /> Reset
                        </button>
                    )}
                    {/* Phase G — selection summary + download button. */}
                    {selectionCount > 0 && (
                        <div className="ml-auto inline-flex items-center gap-2"
                              data-testid="files-selection-bar">
                            <span className="text-[12px] text-[#666]"
                                   data-testid="files-selection-summary">
                                {selectionCount} file{selectionCount===1?"":"s"} · {bytes(selectionTotalBytes)}
                            </span>
                            <button type="button" onClick={clearSelection}
                                     data-testid="files-selection-clear"
                                     className="text-[12px] text-[#666] hover:text-charcoal">
                                Clear
                            </button>
                            <button type="button"
                                     onClick={downloadSelected}
                                     disabled={downloadingBatch || selectionExceedsLimit}
                                     data-testid="files-selection-download"
                                     title="Downloads a single file. Max download size per batch: 500MB. Multiple files will be zipped before download."
                                     className="inline-flex items-center gap-1 text-[12px] px-2 py-1 bg-charcoal text-white rounded-md disabled:opacity-60">
                                <Download className="w-3.5 h-3.5" />
                                {downloadingBatch ? "Preparing…" : `Download ${selectionCount} file${selectionCount===1?"":"s"}`}
                            </button>
                            {selectionExceedsLimit && (
                                <span className="text-[11px] text-terracotta"
                                       data-testid="files-selection-limit-warning">
                                    Selection exceeds 500MB limit
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-manrope font-semibold uppercase tracking-wide text-[#999]">Category</span>
                        {categoryPills.map(({ key, label }) => (
                            <button key={key} type="button"
                                    onClick={()=>setCategoryFilter(categoryFilter===key?null:key)}
                                    data-testid={`files-toolbar-cat-${key}`}
                                    className={`text-[11px] font-manrope font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${categoryFilter===key?"bg-charcoal text-white":"bg-white border border-[#E5E2DC] text-[#666] hover:bg-[#F5F3EE]"}`}>
                                {label}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-manrope font-semibold uppercase tracking-wide text-[#999]">Workstage</span>
                        {WS_PILLS.map((n) => (
                            <button key={String(n)} type="button"
                                    onClick={()=>setWsFilter(wsFilter===n?null:n)}
                                    data-testid={`files-toolbar-ws-${n}`}
                                    className={`text-[11px] font-manrope font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${wsFilter===n?"bg-charcoal text-white":"bg-white border border-[#E5E2DC] text-[#666] hover:bg-[#F5F3EE]"}`}>
                                {n==="unassigned"?"Unassigned":`WS${n}`}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="bg-white border border-[#E5E2DC] rounded-lg overflow-x-auto" data-testid="files-list">
                {loading ? <div className="p-4 text-[#666] text-[13px]">Loading files…</div> :
                groupByWs ? (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                        <div className="flex flex-col divide-y divide-[#E5E2DC]" data-testid="files-list-grouped">
                            {groupedByWs.length === 0 && (
                                <div className="p-4 text-[#666] text-[13px]" data-testid="files-list-empty">No files match the current view.</div>
                            )}
                            {groupedByWs.map(({ ws, ungrouped, subGroups }) => (
                                <WorkstageSection key={String(ws)}
                                                    ws={ws}
                                                    ungrouped={ungrouped}
                                                    subGroups={subGroups}
                                                    ctx={tableCtx}
                                                    expand={expand}
                                                    newGroupWs={newGroupWs}
                                                    newGroupName={newGroupName}
                                                    setNewGroupName={setNewGroupName}
                                                    onOpenAddGroup={openAddGroup}
                                                    onCancelAddGroup={()=>{ setNewGroupWs(null); setNewGroupName(""); }}
                                                    onSubmitNewGroup={submitNewGroup}
                                                    renamingGid={renamingGid}
                                                    setRenamingGid={setRenamingGid}
                                                    renameInput={renameInput}
                                                    setRenameInput={setRenameInput}
                                                    onRenameSubmit={submitRename}
                                                    onDeleteGroup={(g)=>setGroupToDelete(g)}
                                                    onDownloadGroup={downloadGroup} />
                            ))}
                        </div>
                    </DndContext>
                ) : filteredItems.length === 0 ? (
                    <div className="p-4 text-[#666] text-[13px]" data-testid="files-list-empty">No files match the current view.</div>
                ) : (
                    <FileTable rows={sortedItems} ctx={tableCtx} />
                )}
            </div>

            {deleteTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
                     data-testid="files-delete-dialog">
                    <div className="bg-white border border-[#E5E2DC] rounded-lg max-w-md w-full mx-4 p-5 flex flex-col gap-3">
                        <div className="font-manrope font-semibold text-[16px] text-charcoal">Delete file?</div>
                        <div className="text-[13px] text-charcoal">
                            <div className="font-mono break-all" data-testid="files-delete-dialog-filename">{deleteTarget.file.name}</div>
                            <div className="text-[12px] text-[#666] mt-1">
                                Category: {byKey[deleteTarget.file.category]?.label || deleteTarget.file.category || "—"}  ·  WS{deleteTarget.file.originating_workstage || " —"}
                            </div>
                            <div className="text-[12px] text-terracotta mt-2">This cannot be undone.</div>
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            <button type="button" onClick={()=>setDeleteTarget(null)}
                                    data-testid="files-delete-dialog-cancel"
                                    className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-charcoal hover:bg-[#F5F3EE]">
                                Cancel
                            </button>
                            <button type="button" onClick={confirmDelete} disabled={deleting}
                                    data-testid="files-delete-dialog-confirm"
                                    className="text-[13px] px-3 py-1.5 bg-terracotta text-white rounded-md hover:bg-[#8A4535] disabled:opacity-50">
                                {deleting ? "Deleting…" : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {groupToDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
                      data-testid="group-delete-dialog">
                    <div className="bg-white border border-[#E5E2DC] rounded-lg max-w-md w-full mx-4 p-5 flex flex-col gap-3">
                        <div className="font-manrope font-semibold text-[16px] text-charcoal">Delete group?</div>
                        <div className="text-[13px] text-charcoal">
                            Delete group <span className="font-semibold">'{groupToDelete.name}'</span>? This group is empty.
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            <button type="button" onClick={()=>setGroupToDelete(null)}
                                    data-testid="group-delete-cancel"
                                    className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-charcoal hover:bg-[#F5F3EE]">
                                Cancel
                            </button>
                            <button type="button" onClick={confirmDeleteGroup}
                                    data-testid="group-delete-confirm"
                                    className="text-[13px] px-3 py-1.5 bg-terracotta text-white rounded-md hover:bg-[#8A4535]">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showModal && (
                <FileDetailModal
                    projectId={id} fileId={fileId}
                    groups={groups}
                    sortedItems={sortedItems}
                    onNavigate={(nextFid)=>navigate(
                        `/admin/projects/${id}/files/${nextFid}`,
                        { replace: true })}
                    onClose={()=>navigate(`/admin/projects/${id}/files`)}
                    onChanged={refresh} />
            )}
        </div>
    );
}

// =====================================================================
// Workstage section — muted terracotta header, collapsible, contains
// ungrouped files plus sub-groups. Sub-groups live inside the WS.
// =====================================================================
// =====================================================================
// Dispatch 4 — Upload panel.
//
// Collapsible multi-file batch uploader:
//  * Drop zone (click-to-pick OR drag-and-drop confined to the zone).
//  * Shared metadata: category, workstage, group, For Client checkbox.
//  * Group dropdown filtered to the chosen workstage with a trailing
//    "Create new group…" affordance that POSTs immediately.
//  * Pending rows show per-file status + progress bar + remove-X.
//  * Caps: 20 files total, 500MB total, enforced at add time.
// =====================================================================
function UploadPanel({ open, setOpen, pending, setPending,
                         shared, setShared, groups, adminSelectable,
                         projectId, uploading, addFiles, removePending,
                         submitBatch, onGroupCreated, formatBytes,
                         maxFiles, maxBytes }) {
    const [isDragOver, setDragOver] = useState(false);
    const [creatingGroup, setCreatingGroup] = useState(false);
    const [newGroupName, setNewGroupName] = useState("");
    const [creatingGroupPending, setCreatingGroupPending] = useState(false);
    const fileInputRef = useRef(null);
    const totalBytes = pending.reduce((s, p) => s + p.file.size, 0);
    const queuedCount = pending.filter((p) => p.status !== "done").length;

    const onDrop = (e) => {
        e.preventDefault(); e.stopPropagation();
        setDragOver(false);
        addFiles(e.dataTransfer?.files);
    };
    const onDragOver = (e) => {
        e.preventDefault(); e.stopPropagation(); setDragOver(true);
    };
    const onDragLeave = (e) => {
        e.preventDefault(); e.stopPropagation(); setDragOver(false);
    };

    const workstageGroups = groups.filter(
        (g) => g.workstage === shared.originating_workstage);

    const submitNewGroup = async () => {
        const name = newGroupName.trim();
        if (!name) { setCreatingGroup(false); return; }
        setCreatingGroupPending(true);
        try {
            const r = await api.post(
                `/admin/projects/${projectId}/file-groups`,
                { name, workstage: shared.originating_workstage });
            setCreatingGroup(false);
            setNewGroupName("");
            if (r.data?.group) {
                await onGroupCreated(r.data.group);
            }
        } catch (err) {
            const code = err?.response?.data?.detail?.error;
            if (code === "duplicate_group_name") {
                toast.error("Group name already exists in this workstage");
            } else {
                toast.error("Could not create group");
            }
        } finally {
            setCreatingGroupPending(false);
        }
    };

    return (
        <div className="bg-white border border-[#E5E2DC] rounded-lg"
              data-testid="files-upload-panel">
            <button type="button"
                     onClick={()=>setOpen(!open)}
                     data-testid="files-upload-section-toggle"
                     className="w-full flex items-center gap-2 px-4 py-3 text-left">
                {open ? <ChevronDown className="w-4 h-4 text-[#666]" />
                        : <ChevronRight className="w-4 h-4 text-[#666]" />}
                <span className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666]">
                    Upload new files
                </span>
                {!open && pending.length > 0 && (
                    <span className="ml-2 text-[11px] text-[#999]">
                        {pending.length} file{pending.length===1?"":"s"} queued
                    </span>
                )}
            </button>
            {open && (
                <div className="px-4 pb-4 flex flex-col gap-3 border-t border-[#E5E2DC]">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3">
                        <select data-testid="files-upload-workstage"
                                 value={shared.originating_workstage}
                                 onChange={(e)=>setShared((s)=>({
                                     ...s,
                                     originating_workstage: parseInt(e.target.value,10),
                                     group_id: "",  // reset — groups are WS-scoped
                                 }))}
                                 className="border border-[#E5E2DC] rounded-md px-3 py-2 text-[13px]">
                            {Object.entries(WS_NAMES).map(([n,name])=>
                                <option key={n} value={n}>WS {n} · {name}</option>)}
                        </select>
                        <select required data-testid="files-upload-category"
                                 value={shared.category}
                                 onChange={(e)=>setShared((s)=>({...s,category:e.target.value}))}
                                 className="border border-[#E5E2DC] rounded-md px-3 py-2 text-[13px]">
                            <option value="">Content category…</option>
                            {adminSelectable.map((c) => (
                                <option key={c.key} value={c.key}>{c.label}</option>
                            ))}
                        </select>
                        {creatingGroup ? (
                            <div className="flex items-center gap-1">
                                <input autoFocus value={newGroupName}
                                        data-testid="files-upload-new-group-input"
                                        onChange={(e)=>setNewGroupName(e.target.value)}
                                        onKeyDown={(e)=>{
                                            if (e.key==="Enter") { e.preventDefault(); submitNewGroup(); }
                                            if (e.key==="Escape") { setCreatingGroup(false); setNewGroupName(""); }
                                        }}
                                        onBlur={submitNewGroup}
                                        placeholder="New group name…"
                                        disabled={creatingGroupPending}
                                        className="border border-[#E5E2DC] rounded-md px-3 py-2 text-[13px] w-full" />
                            </div>
                        ) : (
                            <select data-testid="files-upload-group"
                                     value={shared.group_id}
                                     onChange={(e)=>{
                                         if (e.target.value === "__new__") {
                                             setCreatingGroup(true); return;
                                         }
                                         setShared((s)=>({...s,group_id:e.target.value}));
                                     }}
                                     className="border border-[#E5E2DC] rounded-md px-3 py-2 text-[13px]">
                                <option value="">Unassigned</option>
                                {workstageGroups.map((g) => (
                                    <option key={g.id} value={g.id}>{g.name}</option>
                                ))}
                                <option value="__new__">+ Create new group…</option>
                            </select>
                        )}
                    </div>
                    <label className="inline-flex items-center gap-2 text-[13px] text-[#666]">
                        <input type="checkbox"
                                data-testid="files-upload-client-visible"
                                checked={shared.client_visible}
                                onChange={(e)=>setShared((s)=>({...s,client_visible:e.target.checked}))} />
                        Mark uploaded files as "For Client"
                    </label>
                    <div onDrop={onDrop} onDragOver={onDragOver}
                          onDragLeave={onDragLeave}
                          onClick={()=>fileInputRef.current?.click()}
                          data-testid="files-upload-dropzone"
                          className={`border-2 border-dashed rounded-md px-4 py-8 text-center cursor-pointer transition-colors ${isDragOver ? "border-charcoal bg-[#FAF8F3]" : "border-[#E5E2DC] bg-[#FDFCF9] hover:bg-[#FAF8F3]"}`}>
                        <Upload className="w-6 h-6 text-[#666] mx-auto mb-2" />
                        <div className="text-[13px] text-charcoal">
                            Drop files here or <span className="underline">browse</span>
                        </div>
                        <div className="text-[11px] text-[#999] mt-1"
                              data-testid="files-upload-cap-helper">
                            Up to {maxFiles} files, {formatBytes(maxBytes)} total per batch.
                        </div>
                        <input ref={fileInputRef} type="file" multiple
                                data-testid="files-upload-input"
                                onChange={(e)=>{ addFiles(e.target.files); e.target.value=""; }}
                                className="hidden" />
                    </div>
                    {pending.length > 0 && (
                        <div className="flex flex-col gap-2"
                              data-testid="files-upload-pending-list">
                            {pending.map((p) => (
                                <div key={p.tempId}
                                      data-testid={`files-upload-pending-${p.tempId}`}
                                      className="flex items-center gap-3 border border-[#E5E2DC] rounded-md px-3 py-2 bg-[#FDFCF9]">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13px] truncate font-medium text-charcoal"
                                              title={p.file.name}>
                                            {p.file.name}
                                        </div>
                                        <div className="text-[11px] text-[#999] flex items-center gap-2">
                                            <span>{formatBytes(p.file.size)}</span>
                                            {p.status === "uploading" && (
                                                <span>· {p.pct}%</span>
                                            )}
                                            {p.status === "done" && (
                                                <span className="text-[#2E7D32]">· Uploaded ✓</span>
                                            )}
                                            {p.status === "failed" && (
                                                <span className="text-terracotta">· Failed: {p.error}</span>
                                            )}
                                        </div>
                                        {p.status === "uploading" && (
                                            <div className="h-1 bg-[#E5E2DC] rounded mt-1">
                                                <div className="h-1 bg-charcoal rounded transition-all"
                                                      style={{width:`${p.pct}%`}} />
                                            </div>
                                        )}
                                    </div>
                                    <button type="button"
                                             onClick={()=>removePending(p.tempId)}
                                             disabled={p.status === "uploading"}
                                             data-testid={`files-upload-pending-remove-${p.tempId}`}
                                             className="text-[#999] hover:text-charcoal p-1 disabled:opacity-30">
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                            <div className="text-[11px] text-[#999]">
                                {queuedCount} queued · {formatBytes(totalBytes)} total
                            </div>
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <button type="button"
                                 onClick={submitBatch}
                                 disabled={uploading || !shared.category || queuedCount === 0}
                                 data-testid="files-upload-submit"
                                 className="inline-flex items-center gap-1.5 px-3 py-2 bg-charcoal text-white text-[13px] rounded-md hover:bg-black disabled:opacity-60">
                            <Upload className="w-4 h-4" />
                            {uploading
                                ? "Uploading…"
                                : queuedCount
                                    ? `Upload ${queuedCount} file${queuedCount===1?"":"s"}`
                                    : "Upload"}
                        </button>
                        {pending.length > 0 && !uploading && (
                            <button type="button"
                                     onClick={()=>setPending([])}
                                     data-testid="files-upload-clear"
                                     className="text-[12px] text-[#666] hover:text-charcoal underline">
                                Clear list
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}


function WorkstageSection({ ws, ungrouped, subGroups, ctx, expand,
                             newGroupWs, newGroupName, setNewGroupName,
                             onOpenAddGroup, onCancelAddGroup,
                             onSubmitNewGroup,
                             renamingGid, setRenamingGid,
                             renameInput, setRenameInput,
                             onRenameSubmit, onDeleteGroup,
                             onDownloadGroup }) {
    const { isOver, setNodeRef } = useDroppable({ id: `ws-${ws}` });
    const collapseKey = `ws-${ws}`;
    const collapsed = expand.isCollapsed(collapseKey);
    const isUnassigned = ws === "unassigned";
    const totalRows = ungrouped.length +
        subGroups.reduce((s, g) => s + g.rows.length, 0);
    const canAddGroup = !isUnassigned;
    const showNewGroupInput = canAddGroup && newGroupWs === ws;
    return (
        <div ref={setNodeRef}
              data-testid={`files-group-ws-${ws}`}
              className={isOver ? "ring-2 ring-charcoal/40 ring-inset" : ""}>
            <div className="px-4 py-2 bg-[#C28779] text-white flex items-center gap-2">
                <button type="button" onClick={()=>expand.toggle(collapseKey)}
                         data-testid={`files-group-ws-${ws}-toggle`}
                         className="text-white/80 hover:text-white p-0.5">
                    {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                <span className="text-[12px] font-manrope font-semibold uppercase tracking-wide">
                    {isUnassigned ? "Unassigned" : `WS ${ws} · ${WS_NAMES[ws]||""}`}  ({totalRows})
                </span>
                {canAddGroup && !showNewGroupInput && (
                    <button type="button" onClick={()=>onOpenAddGroup(ws)}
                             data-testid={`files-group-ws-${ws}-add-group`}
                             className="ml-auto inline-flex items-center gap-1 text-[11px] text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded px-2 py-0.5">
                        <FolderPlus className="w-3.5 h-3.5" /> Add group
                    </button>
                )}
                {showNewGroupInput && (
                    <div className="ml-auto inline-flex items-center gap-1">
                        <input autoFocus value={newGroupName}
                                onChange={(e)=>setNewGroupName(e.target.value)}
                                onKeyDown={(e)=>{ if(e.key==="Enter") onSubmitNewGroup(); if(e.key==="Escape") onCancelAddGroup(); }}
                                placeholder="Group name…"
                                data-testid={`files-group-ws-${ws}-new-group-input`}
                                className="text-[12px] border border-white/40 bg-white text-charcoal rounded-md px-2 py-0.5" />
                        <button type="button" onClick={onSubmitNewGroup}
                                 data-testid={`files-group-ws-${ws}-new-group-submit`}
                                 className="text-[11px] px-2 py-0.5 bg-white text-charcoal rounded">Add</button>
                        <button type="button" onClick={onCancelAddGroup}
                                 data-testid={`files-group-ws-${ws}-new-group-cancel`}
                                 className="text-[11px] px-2 py-0.5 text-white/80">Cancel</button>
                    </div>
                )}
            </div>
            {!collapsed && (
                <>
                    {ungrouped.length === 0 && subGroups.length === 0 ? (
                        <div className="px-4 py-3 text-[12px] text-[#999]" data-testid={`files-group-ws-${ws}-empty`}>No files in this workstage.</div>
                    ) : (
                        <>
                            {totalRows > 0 && (
                                <FileTable
                                    rows={[
                                        ...ungrouped,
                                        ...subGroups.reduce((acc, g) => acc.concat(g.rows), []),
                                    ]}
                                    ctx={ctx} draggable headerOnly
                                    wsScope={ws === "unassigned" ? "unassigned" : `ws${ws}`} />
                            )}
                            {ungrouped.length > 0 && (
                                <FileTable rows={ungrouped} ctx={ctx} draggable showHeader={false} />
                            )}
                            {subGroups.map(({ group, rows }) => (
                                <SubGroupSection key={group.id}
                                                    group={group} rows={rows}
                                                    ctx={ctx} expand={expand}
                                                    renamingGid={renamingGid}
                                                    setRenamingGid={setRenamingGid}
                                                    renameInput={renameInput}
                                                    setRenameInput={setRenameInput}
                                                    onRenameSubmit={onRenameSubmit}
                                                    onDeleteGroup={()=>onDeleteGroup(group)}
                                                    onDownloadGroup={()=>onDownloadGroup(group.id)} />
                            ))}
                        </>
                    )}
                </>
            )}
        </div>
    );
}

// =====================================================================
// Sub-group section — collapsible, droppable, contains its files.
// Styling is subordinate to the WS header (lighter neutral).
// =====================================================================
function SubGroupSection({ group, rows, ctx, expand,
                             renamingGid, setRenamingGid,
                             renameInput, setRenameInput,
                             onRenameSubmit, onDeleteGroup,
                             onDownloadGroup }) {
    const { selected, onToggleSelectAll } = ctx;
    const { isOver, setNodeRef } = useDroppable({ id: `group-${group.id}` });
    const canDelete = rows.length === 0;
    const isRenaming = renamingGid === group.id;
    const collapseKey = `grp-${group.id}`;
    const collapsed = expand.isCollapsed(collapseKey);
    const allSelected = rows.length > 0
        && rows.every((r) => selected.has(r.file.id));
    const someSelected = rows.some((r) => selected.has(r.file.id));
    return (
        <div ref={setNodeRef}
              data-testid={`files-group-custom-${group.id}`}
              className={`border-t border-[#E5E2DC] ${isOver ? "ring-2 ring-charcoal/40 ring-inset" : ""}`}>
            <div className="pl-10 pr-4 py-1.5 bg-[#F0ECE3] flex items-center gap-2">
                <input type="checkbox"
                        data-testid={`files-group-select-${group.id}`}
                        ref={(el)=>{ if(el) el.indeterminate = !allSelected && someSelected; }}
                        checked={allSelected}
                        onChange={()=>onToggleSelectAll(rows)}
                        onClick={(ev)=>ev.stopPropagation()} />
                <button type="button" onClick={()=>expand.toggle(collapseKey)}
                         data-testid={`files-group-custom-${group.id}-toggle`}
                         className="text-[#666] hover:text-charcoal p-0.5">
                    {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                {isRenaming ? (
                    <>
                        <input autoFocus value={renameInput}
                                onChange={(e)=>setRenameInput(e.target.value)}
                                onKeyDown={(e)=>{ if(e.key==="Enter") onRenameSubmit(group.id); if(e.key==="Escape") setRenamingGid(null); }}
                                data-testid={`files-group-rename-input-${group.id}`}
                                className="border border-[#E5E2DC] rounded-md px-2 py-0.5 text-[12px] font-manrope font-semibold uppercase tracking-wide" />
                        <button type="button" onClick={()=>onRenameSubmit(group.id)}
                                data-testid={`files-group-rename-submit-${group.id}`}
                                className="text-[11px] px-2 py-0.5 bg-charcoal text-white rounded">Save</button>
                    </>
                ) : (
                    <button type="button"
                             onClick={()=>{ setRenameInput(group.name); setRenamingGid(group.id); }}
                             data-testid={`files-group-header-${group.id}`}
                             className="text-[11px] font-manrope font-semibold uppercase tracking-wide text-charcoal hover:underline">
                        {group.name}
                    </button>
                )}
                <span className="text-[11px] text-[#666]">({rows.length})</span>
                {rows.length > 0 && (
                    <button type="button" onClick={onDownloadGroup}
                             data-testid={`files-group-download-${group.id}`}
                             title="Downloads all files in this group as a zip. Max download size per batch: 500MB."
                             className="ml-auto text-[#666] hover:text-charcoal p-1">
                        <Download className="w-3.5 h-3.5" />
                    </button>
                )}
                {canDelete && (
                    <button type="button" onClick={onDeleteGroup}
                             data-testid={`files-group-delete-${group.id}`}
                             title="Delete group (empty)"
                             className={`${rows.length === 0 ? "ml-auto" : ""} text-[#999] hover:text-terracotta p-1`}>
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
            {!collapsed && (
                rows.length === 0 ? (
                    <div className="pl-10 pr-4 py-3 text-[12px] text-[#999]" data-testid={`files-group-custom-${group.id}-empty`}>Drop files here, or empty — delete group above.</div>
                ) : (
                    <FileTable rows={rows} ctx={ctx} draggable showHeader={false} />
                )
            )}
        </div>
    );
}

function _arrow(active, dir) {
    if (!active) return "";
    return dir === "asc" ? " ↑" : " ↓";
}

// =====================================================================
// FileTable — list of rows.  Uses `table-fixed` + explicit percentage
// column widths so every section aligns.  Each `<td>` in the File
// column truncates with a `title` tooltip for overflow.
// =====================================================================
function FileTable({ rows, ctx, draggable, showHeader = true, headerOnly = false, wsScope }) {
    const { byKey, onHeaderClick, sortKey, sortDir,
             selected, onToggleSelectAll } = ctx;
    const allSelected = rows.length > 0
        && rows.every((r) => selected.has(r.file.id));
    const someSelected = rows.some((r) => selected.has(r.file.id));
    const selectAllTestid = wsScope
        ? `files-table-select-all-${wsScope}`
        : "files-table-select-all";
    return (
        <table className="w-full table-fixed text-[13px] text-left">
            <colgroup>
                <col style={{ width: "3%" }} />
                {draggable && <col style={{ width: "3%" }} />}
                <col style={{ width: "22%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "5%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "6%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "6%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "6%" }} />
            </colgroup>
            {showHeader && (
                <thead className="text-[#666]"><tr>
                    <th className="py-2 px-3 border-b border-[#E5E2DC]">
                        <input type="checkbox"
                                ref={(el)=>{ if(el) el.indeterminate = !allSelected && someSelected; }}
                                checked={allSelected}
                                onChange={()=>onToggleSelectAll(rows)}
                                data-testid={selectAllTestid} />
                    </th>
                    {draggable && <th className="py-2 px-2 border-b border-[#E5E2DC]"></th>}
                    <th onClick={()=>onHeaderClick("name")} className="font-medium py-2 px-4 border-b border-[#E5E2DC] cursor-pointer select-none">File{_arrow(sortKey==="name", sortDir)}</th>
                    <th onClick={()=>onHeaderClick("category")} className="font-medium py-2 px-4 border-b border-[#E5E2DC] cursor-pointer select-none">Category{_arrow(sortKey==="category", sortDir)}</th>
                    <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">Source</th>
                    <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">WS</th>
                    <th onClick={()=>onHeaderClick("document_number")} className="font-medium py-2 px-4 border-b border-[#E5E2DC] cursor-pointer select-none">Document #{_arrow(sortKey==="document_number", sortDir)}</th>
                    <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">Current V</th>
                    <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">Filetype</th>
                    <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">Publication</th>
                    <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">For Client</th>
                    <th onClick={()=>onHeaderClick("updated_at")} className="font-medium py-2 px-4 border-b border-[#E5E2DC] cursor-pointer select-none">Updated{_arrow(sortKey==="updated_at", sortDir)}</th>
                    <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">Actions</th>
                </tr></thead>
            )}
            {!headerOnly && (
                <tbody>
                    {rows.map(({ file, versions }) => (
                        <FileRow key={file.id} file={file} versions={versions}
                                  ctx={ctx} byKey={byKey}
                                  draggable={Boolean(draggable)} />
                    ))}
                </tbody>
            )}
        </table>
    );
}

// =====================================================================
// InlineText — click-to-edit text cell used in FileRow name + docnum.
// Enter commits, Escape cancels, blur commits.  When `locked`, renders
// read-only text with a tooltip explaining the lock state.
// =====================================================================
function InlineText({ value, onCommit, locked, placeholder,
                        testid, inputTestid, lockedTitle, allowEmpty,
                        className }) {
    const [editing, setEditing] = useState(false);
    const [buf, setBuf] = useState(value || "");
    useEffect(() => { setBuf(value || ""); }, [value]);
    if (locked) {
        return (
            <span data-testid={testid}
                   title={lockedTitle || "Locked"}
                   className={`text-[#666] ${className||""}`}>
                {value || placeholder || "—"}
            </span>
        );
    }
    if (!editing) {
        return (
            <span data-testid={testid}
                   onClick={()=>setEditing(true)}
                   title="Click to edit"
                   className={`cursor-text ${className||""} ${value?"":"text-[#999]"}`}>
                {value || placeholder || "—"}
            </span>
        );
    }
    const commit = () => {
        setEditing(false);
        const next = buf.trim();
        if (!allowEmpty && !next) { setBuf(value || ""); return; }
        if (next === (value || "")) return;
        onCommit(next);
    };
    return (
        <input autoFocus value={buf}
                data-testid={inputTestid}
                onChange={(e)=>setBuf(e.target.value)}
                onBlur={commit}
                onKeyDown={(e)=>{
                    if (e.key === "Enter") { e.preventDefault(); commit(); }
                    if (e.key === "Escape") { setBuf(value || ""); setEditing(false); }
                }}
                className="border border-[#E5E2DC] rounded-md px-2 py-0.5 text-[12px] bg-white outline-none focus:border-charcoal w-full" />
    );
}


function FileRow({ file, versions, ctx, byKey, draggable }) {
    const { id, adminSelectable, onPatch, openDelete, navigate,
             selected, onToggleSelect, onDownloadSingle } = ctx;
    const cur = (versions||[]).find(v=>v.version_state==="Current") || versions?.[versions.length-1];
    const canDelete = !file.immutable;
    const sourceOrigin = file.source_origin;
    const rowDnd = useDraggable({ id: `row-${file.id}`, disabled: !draggable });
    const style = rowDnd.transform ? {
        transform: `translate3d(${rowDnd.transform.x}px, ${rowDnd.transform.y}px, 0)`,
        zIndex: 20, position: "relative",
    } : undefined;
    const locked = Boolean(file.immutable) || file.category === "deliverables";
    const nameLocked = Boolean(file.immutable);
    const isSelected = selected.has(file.id);
    const ft = fileTypeDisplay(file, cur);
    return (
        <tr data-testid={`file-row-${file.id}`}
             ref={draggable ? rowDnd.setNodeRef : undefined}
             style={style}
             className="hover:bg-[#FAFAFA] border-t border-[#E5E2DC]">
            <td className="py-2 px-3" onClick={(ev)=>ev.stopPropagation()}>
                <input type="checkbox"
                        checked={isSelected}
                        onChange={()=>onToggleSelect({ file, versions })}
                        data-testid={`file-select-${file.id}`} />
            </td>
            {draggable && (
                <td className="py-2 px-2" onClick={(ev)=>ev.stopPropagation()}>
                    <button type="button"
                             {...rowDnd.listeners} {...rowDnd.attributes}
                             data-testid={`file-drag-handle-${file.id}`}
                             title="Drag to move"
                             className="text-[#999] cursor-grab active:cursor-grabbing p-1">
                        <GripVertical className="w-4 h-4" />
                    </button>
                </td>
            )}
            <td className="py-2 px-4 truncate" title={file.name} onClick={(ev)=>ev.stopPropagation()}>
                <div className="flex items-center gap-2 min-w-0">
                    <TypeIcon mime={cur?.mime_type} />
                    <InlineText value={file.name || ""}
                                 locked={nameLocked}
                                 placeholder="(untitled)"
                                 testid={`file-name-${file.id}`}
                                 inputTestid={`file-name-input-${file.id}`}
                                 lockedTitle="Name is locked on immutable files"
                                 onCommit={(next)=>onPatch(file.id, {
                                     name: next,
                                 }, { optimistic: true })}
                                 className="font-semibold truncate" />
                </div>
            </td>
            <td className="py-2 px-4 truncate" onClick={(ev)=>ev.stopPropagation()}>
                {locked ? (
                    <span data-testid={`file-category-label-${file.id}`}
                           title={file.category === "deliverables"
                                   ? "Deliverable category is managed by the deliverable workflow"
                                   : "Category is set by the file's origin"}
                           className="inline-block text-[11px] font-manrope font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#EBE8E0] text-[#666]">
                        {byKey[file.category]?.label || file.category || "—"}
                    </span>
                ) : (
                    <select data-testid={`file-category-select-${file.id}`}
                             value={file.category || ""}
                             onChange={(e)=>onPatch(file.id, {
                                 category: e.target.value,
                             }, { optimistic: true })}
                             className="w-full border border-[#E5E2DC] rounded-md px-2 py-1 text-[12px] bg-white">
                        {(!file.category) && <option value="" disabled>—</option>}
                        {(adminSelectable||[]).map((c) => (
                            <option key={c.key} value={c.key}>{c.label}</option>
                        ))}
                    </select>
                )}
            </td>
            <td className="py-2 px-4 truncate" title={sourceOriginLabel(sourceOrigin)}>
                {sourceOrigin
                    ? <span data-testid={`file-source-${file.id}`}
                            className="inline-block text-[10px] font-manrope font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#F5F3EE] text-[#666]">
                        {sourceOriginLabel(sourceOrigin)}
                      </span>
                    : "—"}
            </td>
            <td className="py-2 px-4" onClick={(ev)=>ev.stopPropagation()}>
                <select data-testid={`file-ws-select-${file.id}`}
                         value={file.originating_workstage ?? ""}
                         onChange={(e)=>onPatch(file.id, {
                             originating_workstage: e.target.value === ""
                                 ? null : parseInt(e.target.value, 10),
                         }, { optimistic: true })}
                         className="w-full border border-[#E5E2DC] rounded-md px-1 py-1 text-[12px] bg-white">
                    <option value="">—</option>
                    {[1,2,3,4,5,6,7].map((n)=><option key={n} value={n}>WS{n}</option>)}
                </select>
            </td>
            <td className="py-2 px-4 truncate" title={cur?.document_number || "—"} onClick={(ev)=>ev.stopPropagation()}>
                <InlineText value={cur?.document_number || ""}
                             locked={nameLocked}
                             placeholder="—"
                             testid={`file-docnum-${file.id}`}
                             inputTestid={`file-docnum-input-${file.id}`}
                             lockedTitle="Document number is locked on immutable files"
                             allowEmpty
                             onCommit={(next)=>onPatch(file.id, {
                                 document_number: next || null,
                             }, { optimistic: false })}
                             className="truncate" />
            </td>
            <td className="py-2 px-4">v{cur?.version_number || "—"}</td>
            <td className="py-2 px-4 truncate" title={ft} data-testid={`file-filetype-${file.id}`}>{ft}</td>
            <td className="py-2 px-4">{pubStatePill(cur?.publication_state)}</td>
            <td className="py-2 px-4" onClick={(ev)=>ev.stopPropagation()}>
                <input type="checkbox"
                        data-testid={`file-visible-${file.id}`}
                        checked={Boolean(cur?.client_visible)}
                        onChange={(e)=>onPatch(file.id, {
                            client_visible: e.target.checked,
                        }, { optimistic: true })} />
            </td>
            <td className="py-2 px-4 text-[#666] truncate" title={fmt(file.updated_at)}>{fmt(file.updated_at)}</td>
            <td className="py-2 px-4" onClick={(ev)=>ev.stopPropagation()}>
                <div className="inline-flex items-center gap-1">
                    <button type="button"
                            onClick={()=>navigate(`/admin/projects/${id}/files/${file.id}`)}
                            data-testid={`admin-files-row-view-${file.id}`}
                            title="View details"
                            className="text-[#999] hover:text-charcoal p-1 rounded">
                        <Eye className="w-4 h-4" />
                    </button>
                    <button type="button"
                            onClick={()=>onDownloadSingle(file.id)}
                            data-testid={`files-row-download-${file.id}`}
                            title="Downloads a single file. Max download size per batch: 500MB. Multiple files will be zipped before download."
                            className="text-[#999] hover:text-charcoal p-1 rounded">
                        <Download className="w-4 h-4" />
                    </button>
                    {canDelete && (
                        <button type="button"
                                onClick={(ev)=>openDelete({ file, versions }, ev)}
                                data-testid={`files-row-delete-${file.id}`}
                                title="Delete file"
                                className="text-[#999] hover:text-terracotta p-1 rounded">
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );
}

// =====================================================================
// File detail body — shared between the full-page route and the modal.
// Phase D: two-column layout (preview + side panel w/ Version State).
// =====================================================================
export function FileDetailBody({ projectId, fileId, groups, onChanged, embedded }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [uploadVer, setUploadVer] = useState(null);
    const [reasonVer, setReasonVer] = useState("");
    const [issueMeta, setIssueMeta] = useState({ related_record_type:"", related_record_id:"" });

    const load = async () => {
        setLoading(true); setError(null);
        try { const r = await api.get(`/admin/projects/${projectId}/files/${fileId}`); setData(r.data); }
        catch { setError("Could not load file"); }
        finally { setLoading(false); }
    };
    useEffect(()=>{ load(); /* eslint-disable-next-line */ }, [projectId, fileId]);

    if (loading) return <div className="text-[#666]" data-testid="file-detail-loading">Loading file…</div>;
    if (error || !data) return <div data-testid="file-detail-error" className="text-[#7A5148]">{error||"File not found"}</div>;

    const versions = (data.versions||[]).slice().sort((a,b)=>b.version_number-a.version_number);
    const current = versions.find(v=>v.version_state==="Current") || versions[0];
    const previewUrl = current ? `${API}/admin/files/versions/${current.id}/preview` : null;
    const isPreviewable = (m)=>!!m && (m.startsWith("image/") || m === "application/pdf");
    const isImage = (m)=>!!m && m.startsWith("image/");
    const isPdf = (m)=>m === "application/pdf";
    const EXECUTED_CATEGORIES = ["executed_agreement", "executed_contract", "executed_change_instrument"];
    const isExecutedDoc = EXECUTED_CATEGORIES.includes(data.file.category);
    const MIN_REASON_LEN = 20;
    // Filter groups to those in the file's workstage.
    const applicableGroups = (groups||[]).filter(
        (g) => g.workstage === data.file.originating_workstage);

    const doNewVersion = async () => {
        if (!uploadVer) return;
        if (isExecutedDoc && (reasonVer || "").trim().length < MIN_REASON_LEN) {
            setError(`Reason must be at least ${MIN_REASON_LEN} characters for executed documents.`);
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("expected_current_version", String(current.version_number));
            fd.append("upload", uploadVer);
            if (isExecutedDoc) {
                fd.append("reason", (reasonVer || "").trim());
                await api.post(`/admin/projects/${projectId}/files/${fileId}/executed/new-version`, fd);
            } else {
                await api.post(`/admin/projects/${projectId}/files/${fileId}/versions`, fd);
            }
            setUploadVer(null); setReasonVer(""); await load();
            if (onChanged) await onChanged();
        } catch (e) {
            const d = e?.response?.data?.detail;
            setError(typeof d === "string" ? d
                     : d?.error ? `${d.error}: ${d.reason || d.message || ""}`
                     : "Upload failed");
        }
        finally { setBusy(false); }
    };
    const doPublish = async () => {
        setBusy(true);
        try { await api.post(`/admin/files/${fileId}/versions/${current.id}/publish`); await load(); if (onChanged) await onChanged(); }
        catch(e){ setError(e?.response?.data?.detail||"Publish failed"); }
        finally{ setBusy(false); }
    };
    const doIssue = async () => {
        if (!issueMeta.related_record_type || !issueMeta.related_record_id) { setError("Related record type and id required"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("related_record_type", issueMeta.related_record_type);
            fd.append("related_record_id", issueMeta.related_record_id);
            await api.post(`/admin/files/${fileId}/versions/${current.id}/issue`, fd);
            await load(); if (onChanged) await onChanged();
        } catch(e){ setError(e?.response?.data?.detail||"Issue failed"); }
        finally{ setBusy(false); }
    };
    const changeGroup = async (newGid) => {
        try {
            await api.patch(`/admin/projects/${projectId}/files/${fileId}`,
                             { group_id: newGid || null });
            await load();
            if (onChanged) await onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.reason || "Could not change group");
        }
    };

    const canPublish = current && current.publication_state === "Internal Draft" && current.version_state === "Current";
    const canIssue = current && current.publication_state === "Published" && current.version_state === "Current";

    return (
        <div className="flex flex-col gap-4" data-testid="file-detail-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                    <h2 className="font-manrope text-xl font-semibold text-charcoal truncate" title={data.file.name} data-testid="file-detail-name">
                        <InlineText value={data.file.name || ""}
                                     locked={Boolean(data.file.immutable)}
                                     placeholder="(untitled)"
                                     testid="file-detail-name-label"
                                     inputTestid="file-detail-name-input"
                                     lockedTitle="Name is locked on immutable files"
                                     onCommit={async (next)=>{
                                         try {
                                             await api.patch(
                                                 `/admin/projects/${projectId}/files/${fileId}`,
                                                 { name: next });
                                             await load();
                                             if (onChanged) await onChanged();
                                         } catch (e) {
                                             toast.error(
                                                 e?.response?.data?.detail?.reason
                                                 || "Could not rename");
                                         }
                                     }}
                                     className="font-manrope text-xl font-semibold text-charcoal" />
                    </h2>
                    <div className="text-[13px] text-[#666] mt-1">
                        WS {data.file.originating_workstage} · {WS_NAMES[data.file.originating_workstage]||"—"} · {data.file.category||"Uncategorised"}
                    </div>
                    <div className="mt-2 text-[12px] text-[#666] flex items-center gap-2">
                        <span>Group:</span>
                        <select data-testid="file-detail-group-select"
                                value={data.file.group_id || ""}
                                onChange={(e)=>changeGroup(e.target.value)}
                                className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[12px] bg-white">
                            <option value="">(ungrouped)</option>
                            {applicableGroups.map((g)=>(
                                <option key={g.id} value={g.id}>{g.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {current && versionStatePill(current.version_state)}
                    {current && pubStatePill(current.publication_state)}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4 items-start">
                <div className="bg-white border border-[#E5E2DC] rounded-lg overflow-hidden" data-testid="file-detail-preview">
                    {current && isPreviewable(current.mime_type) ? (
                        isImage(current.mime_type) ? (
                            <img src={previewUrl} alt={data.file.name} className="w-full max-h-[70vh] object-contain bg-[#FAFAFA]" data-testid="file-preview-image" />
                        ) : isPdf(current.mime_type) ? (
                            <iframe title={data.file.name} src={previewUrl} className="w-full h-[70vh] bg-[#FAFAFA]" data-testid="file-preview-pdf" />
                        ) : null
                    ) : (
                        <div className="p-8 text-center text-[#666]" data-testid="file-preview-unavailable">
                            <div className="mb-2"><FileText className="w-8 h-8 inline text-[#999]" /></div>
                            Preview not available for this file type. Use the download control below.
                        </div>
                    )}
                    {current && (
                        <div className="border-t border-[#E5E2DC] p-3 flex items-center gap-2">
                            <a data-testid="file-detail-download" href={`${API}/admin/files/versions/${current.id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] border border-[#E5E2DC] rounded-md hover:bg-[#FAFAFA]">
                                <Download className="w-4 h-4" /> Download v{current.version_number}
                            </a>
                            {isPreviewable(current.mime_type) && (
                                <a data-testid="file-detail-open-preview" href={previewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] border border-[#E5E2DC] rounded-md hover:bg-[#FAFAFA]">
                                    <Eye className="w-4 h-4" /> Open preview
                                </a>
                            )}
                        </div>
                    )}
                </div>

                <aside className="flex flex-col gap-4">
                    {/* Phase D — Version State retained here (removed from main table). */}
                    <section className="bg-white border border-[#E5E2DC] rounded-lg p-4" data-testid="file-detail-metadata">
                        <div className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666] mb-2">Metadata</div>
                        <dl className="text-[12px] text-charcoal grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                            <dt className="text-[#666]">Version state</dt>
                            <dd data-testid="file-detail-version-state">{current ? versionStatePill(current.version_state) : "—"}</dd>
                            <dt className="text-[#666]">Filetype</dt>
                            <dd data-testid="file-detail-filetype">{fileTypeDisplay(data.file, current)}</dd>
                            <dt className="text-[#666]">Source</dt>
                            <dd data-testid="file-detail-source">{sourceOriginLabel(data.file.source_origin)}</dd>
                            <dt className="text-[#666]">Document #</dt>
                            <dd data-testid="file-detail-docnum">
                                <InlineText value={current?.document_number || ""}
                                             locked={Boolean(data.file.immutable)}
                                             placeholder="—"
                                             allowEmpty
                                             testid="file-detail-docnum-label"
                                             inputTestid="file-detail-docnum-input"
                                             lockedTitle="Document number is locked on immutable files"
                                             onCommit={async (next)=>{
                                                 try {
                                                     await api.patch(
                                                         `/admin/projects/${projectId}/files/${fileId}`,
                                                         { document_number: next || null });
                                                     await load();
                                                     if (onChanged) await onChanged();
                                                 } catch (e) {
                                                     toast.error(
                                                         e?.response?.data?.detail?.reason
                                                         || "Could not update");
                                                 }
                                             }} />
                            </dd>
                            <dt className="text-[#666]">Updated</dt>
                            <dd>{fmt(data.file.updated_at)}</dd>
                        </dl>
                    </section>

                    <section className="bg-white border border-[#E5E2DC] rounded-lg p-4" data-testid="file-detail-versions">
                        <div className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666] mb-2">Version history</div>
                        <ul className="flex flex-col gap-2">
                            {versions.map(v => (
                                <li key={v.id} className="flex items-center gap-2 text-[13px]" data-testid={`file-detail-version-${v.version_number}`}>
                                    <span className="font-manrope font-semibold">v{v.version_number}</span>
                                    {versionStatePill(v.version_state)}
                                    {pubStatePill(v.publication_state)}
                                    <span className="text-[#666] text-[12px] ml-auto">{bytes(v.size)}</span>
                                    <a href={`${API}/admin/files/versions/${v.id}/download`} target="_blank" rel="noreferrer" title={`Download v${v.version_number}`} data-testid={`file-detail-version-download-${v.version_number}`} className="p-1 rounded hover:bg-[#FAFAFA]">
                                        <Download className="w-3.5 h-3.5" />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <section className="bg-white border border-[#E5E2DC] rounded-lg p-4 flex flex-col gap-2" data-testid="file-detail-actions">
                        <div className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666]">Actions</div>
                        <label className="text-[12px] text-[#666]">Upload new version</label>
                        <input type="file" data-testid="file-detail-version-file" onChange={e=>setUploadVer(e.target.files?.[0]||null)} className="text-[13px]" />
                        {isExecutedDoc && (
                            <>
                                <label className="text-[12px] text-[#666] mt-1">
                                    Reason <span className="text-[11px] text-[#8A8A84]">(required · min {MIN_REASON_LEN} characters)</span>
                                </label>
                                <textarea
                                    data-testid="file-detail-version-reason"
                                    value={reasonVer}
                                    onChange={e=>setReasonVer(e.target.value)}
                                    placeholder="Why is a new version of this executed document being uploaded? e.g. original signed PDF scan unreadable; replacing with higher-resolution copy."
                                    rows={3}
                                    className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] resize-y" />
                                <div className="text-[11px] text-[#8A8A84]">
                                    {reasonVer.trim().length}/{MIN_REASON_LEN} characters.
                                    Logged against this file in the project activity trail.
                                </div>
                            </>
                        )}
                        <button
                            disabled={busy || !uploadVer || (isExecutedDoc && reasonVer.trim().length < MIN_REASON_LEN)}
                            onClick={doNewVersion}
                            data-testid="file-detail-version-submit"
                            className="self-start px-3 py-1.5 bg-charcoal text-white text-[13px] rounded-md disabled:opacity-60">
                            {isExecutedDoc ? "Upload new executed version" : "Upload new version"}
                        </button>
                        <div className="h-px bg-[#E5E2DC] my-2" />
                        <button disabled={busy||!canPublish} onClick={doPublish} data-testid="file-detail-publish" className="self-start px-3 py-1.5 border border-[#E5E2DC] text-[13px] rounded-md hover:bg-[#FAFAFA] disabled:opacity-60">Publish current version</button>
                        <div className="h-px bg-[#E5E2DC] my-2" />
                        <label className="text-[12px] text-[#666]">Formally Issue against a linked record</label>
                        <input data-testid="file-detail-issue-type" placeholder="Related record type (e.g. proposal)" value={issueMeta.related_record_type} onChange={e=>setIssueMeta(m=>({...m,related_record_type:e.target.value}))} className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]" />
                        <input data-testid="file-detail-issue-id" placeholder="Related record id" value={issueMeta.related_record_id} onChange={e=>setIssueMeta(m=>({...m,related_record_id:e.target.value}))} className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]" />
                        <button disabled={busy||!canIssue} onClick={doIssue} data-testid="file-detail-issue" className="self-start px-3 py-1.5 border border-[#E5E2DC] text-[13px] rounded-md hover:bg-[#FAFAFA] disabled:opacity-60">Formally Issue</button>
                    </section>

                    {data.links && data.links.length > 0 && (
                        <section className="bg-white border border-[#E5E2DC] rounded-lg p-4" data-testid="file-detail-links">
                            <div className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666] mb-2">Linked records</div>
                            <ul className="flex flex-col gap-1 text-[13px]">
                                {data.links.map((l, i) => (
                                    <li key={i} data-testid={`file-detail-link-${i}`}>{l.relationship_type || "link"} · {l.target_type || "?"} · {l.target_id || "—"}</li>
                                ))}
                            </ul>
                        </section>
                    )}
                    {error && <div className="text-[12px] text-terracotta" data-testid="file-detail-error-msg">{String(error)}</div>}
                </aside>
            </div>
        </div>
    );
}

// =====================================================================
// File detail modal — Phase D wider layout (90vw / max 1200px) +
// sub-group navigation (add-on): prev/next chevrons, arrow-key
// shortcuts, filename + "N of M" indicator.  Nav is strictly scoped
// to sub-group siblings — files with `group_id === null` get no arrows.
// =====================================================================
function FileDetailModal({ projectId, fileId, groups, sortedItems,
                            onNavigate, onClose, onChanged }) {
    // Resolve the currently-open file + its sibling list from the
    // table's current sort order.  Siblings = rows sharing the same
    // non-null `group_id` as the open row.  If the open file isn't
    // (yet) in the list, or is ungrouped, siblings is empty.
    const openEntry = (sortedItems || []).find(
        (e) => e.file.id === fileId);
    const groupId = openEntry?.file?.group_id || null;
    const siblings = groupId
        ? (sortedItems || []).filter((e) => e.file.group_id === groupId)
        : [];
    const idx = siblings.findIndex((e) => e.file.id === fileId);
    const hasNav = Boolean(groupId) && siblings.length > 1 && idx >= 0;
    const prevFid = hasNav && idx > 0 ? siblings[idx - 1].file.id : null;
    const nextFid = hasNav && idx < siblings.length - 1 ? siblings[idx + 1].file.id : null;
    const openFilename = openEntry?.file?.name;

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") { onClose(); return; }
            if (!hasNav) return;
            if (e.key === "ArrowLeft" && prevFid) {
                e.preventDefault(); onNavigate(prevFid);
            } else if (e.key === "ArrowRight" && nextFid) {
                e.preventDefault(); onNavigate(nextFid);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose, onNavigate, hasNav, prevFid, nextFid]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
              data-testid="file-detail-modal"
              onClick={(e)=>{ if (e.target === e.currentTarget) onClose(); }}>
            {/* Fixed outer frame — identical pattern to the portal
                FileDetailModal.  Prevents content-driven resize on
                navigation / tab switches. */}
            <div className="bg-[#F5F3EE] border border-[#E5E2DC] rounded-lg shadow-2xl w-[90vw] max-w-[1200px] h-[92vh] p-5 flex flex-col gap-3 overflow-hidden">
                <div className="flex items-center justify-between gap-3 shrink-0">
                    <div className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666]">File detail</div>
                    {hasNav && (
                        <div className="flex items-center gap-2 ml-auto"
                              data-testid="file-detail-modal-nav">
                            <button type="button"
                                     onClick={()=>prevFid && onNavigate(prevFid)}
                                     disabled={!prevFid}
                                     data-testid="file-detail-modal-prev"
                                     title="Previous (←)"
                                     className="text-[#666] hover:text-charcoal p-1 disabled:opacity-30 disabled:cursor-not-allowed">
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <span className="text-[12px] text-[#666] min-w-0 truncate max-w-[280px]"
                                   title={openFilename}
                                   data-testid="file-detail-modal-position">
                                <span className="font-manrope font-semibold">{idx + 1} of {siblings.length}</span>
                                {openFilename ? <span className="ml-2 text-[#999]">· {openFilename}</span> : null}
                            </span>
                            <button type="button"
                                     onClick={()=>nextFid && onNavigate(nextFid)}
                                     disabled={!nextFid}
                                     data-testid="file-detail-modal-next"
                                     title="Next (→)"
                                     className="text-[#666] hover:text-charcoal p-1 disabled:opacity-30 disabled:cursor-not-allowed">
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    )}
                    <a href={`${API}/admin/files/${fileId}/download`}
                        target="_blank" rel="noreferrer"
                        data-testid="file-detail-modal-download"
                        title="Download this file"
                        className={`${hasNav ? "" : "ml-auto"} text-[#666] hover:text-charcoal p-1`}>
                        <Download className="w-5 h-5" />
                    </a>
                    <button type="button" onClick={onClose}
                             data-testid="file-detail-modal-close"
                             className="text-[#666] hover:text-charcoal p-1">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="flex-1 min-h-0 overflow-auto">
                    <FileDetailBody projectId={projectId} fileId={fileId}
                                     groups={groups} onChanged={onChanged}
                                     embedded />
                </div>
            </div>
        </div>
    );
}

export function ProjectCommunications() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    usePageMeta({ title: "Communications · Project | Studio G4 Admin" });
    useEffect(()=>{ (async ()=>{
        try{
            const r = await api.get(`/admin/projects/${id}/comms-and-notes?limit=100`);
            setItems(r.data?.items||[]);
        } finally { setLoading(false); }
    })(); }, [id]);
    const TYPE_LABEL = { email: "✉ Email", call: "☏ Call", note: "🗒 Note" };
    return (
        <div className="flex flex-col gap-4" data-testid="communications-page">
            <button onClick={()=>navigate(`/admin/projects/${id}`)} data-testid="communications-back" className="self-start inline-flex items-center gap-1.5 text-[13px] text-[#666] hover:text-charcoal">
                <ArrowLeft className="w-4 h-4" /> Back to project workspace
            </button>
            <h1 className="font-manrope text-2xl font-semibold text-charcoal">Communications and Notes</h1>
            <div className="text-[12px] text-[#666] italic">Unified timeline · emails, calls and internal notes interleaved, newest first.</div>
            <div className="bg-white border border-[#E5E2DC] rounded-lg overflow-x-auto">
                {loading ? <div className="p-4 text-[#666] text-[13px]">Loading…</div> :
                items.length===0 ? <div className="p-4 text-[#666] text-[13px]" data-testid="communications-empty">No communications or notes yet.</div> :
                <ul className="flex flex-col divide-y divide-[#E5E2DC]">
                    {items.map((r, idx)=>{
                        const m = r.meta || {};
                        return (
                        <li key={r.ref_id || idx} data-testid={`comms-notes-row-${r.ref_id || idx}`} className="p-4 flex flex-col gap-1">
                            <div className="flex items-center gap-2 text-[12px] text-[#666]">
                                <span className="uppercase font-manrope font-semibold" data-testid={`comms-notes-row-${r.ref_id || idx}-type`}>
                                    {TYPE_LABEL[r.type] || r.type}
                                </span>
                                {m.source === "external_send" && (
                                    <span className="inline-flex rounded-full bg-[#F0EBE1] px-2 py-0.5 text-[10px] uppercase tracking-wide text-[#5A4B2A] font-semibold"
                                          data-testid={`comms-notes-row-${r.ref_id || idx}-ext-badge`}>
                                        Sent externally
                                    </span>
                                )}
                                {m.direction && <><span>·</span><span>{m.direction}</span></>}
                                {m.channel && <><span>·</span><span>{m.channel}</span></>}
                                {m.message_class && <><span>·</span><span>{m.message_class}</span></>}
                                <span className="ml-auto">{fmt(r.ts)}</span>
                            </div>
                            {r.type === "email" ? (
                                <>
                                    <div className="font-semibold text-charcoal text-[14px]">{m.subject || (m.source === "external_send" ? `External send · ${m.template_code || "communication"}` : "(no subject)")}</div>
                                    {m.body && <div className="text-[13px] text-[#333] whitespace-pre-wrap">{m.body}</div>}
                                    {m.source === "external_send" && m.sent_externally_notes && (
                                        <div className="text-[12px] text-[#555] italic" data-testid={`comms-notes-row-${r.ref_id || idx}-ext-notes`}>
                                            Notes: {m.sent_externally_notes}
                                        </div>
                                    )}
                                    {(m.linked_file_ids||[]).length>0 && <div className="text-[12px] text-[#666]">Attachments: {m.linked_file_ids.length}</div>}
                                    {m.provider_message_id && <div className="text-[11px] text-[#999]">Provider id · {m.provider_message_id}</div>}
                                </>
                            ) : (
                                <div className="text-[13px] text-[#333] whitespace-pre-wrap break-words">
                                    {m.body || <span className="text-[#999]">—</span>}
                                </div>
                            )}
                            {r.by && <div className="text-[11px] text-[#999]">By {r.type === "note" ? (r.by_display_name || r.by) : r.by}</div>}
                        </li>);
                    })}
                </ul>}
            </div>
        </div>
    );
}

export function ProjectActivity() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    usePageMeta({ title: "Activity · Project | Studio G4 Admin" });
    useEffect(()=>{ (async ()=>{ try{ const r = await api.get(`/admin/projects/${id}/activity`); setItems(r.data?.items||[]); } finally { setLoading(false); }})(); }, [id]);
    return (
        <div className="flex flex-col gap-4" data-testid="activity-page">
            <button onClick={()=>navigate(`/admin/projects/${id}`)} data-testid="activity-back" className="self-start inline-flex items-center gap-1.5 text-[13px] text-[#666] hover:text-charcoal">
                <ArrowLeft className="w-4 h-4" /> Back to project workspace
            </button>
            <h1 className="font-manrope text-2xl font-semibold text-charcoal">Activity</h1>
            <div className="text-[12px] text-[#666] italic">Append-only, non-editable audit stream.</div>
            <div className="bg-white border border-[#E5E2DC] rounded-lg overflow-x-auto">
                {loading ? <div className="p-4 text-[#666] text-[13px]">Loading…</div> :
                items.length===0 ? <div className="p-4 text-[#666] text-[13px]" data-testid="activity-empty">No activity yet.</div> :
                <ul className="flex flex-col divide-y divide-[#E5E2DC]">
                    {items.map(a=>(
                        <li key={a.id} data-testid={`activity-row-${a.id}`} className="p-3 flex items-start gap-3">
                            <span className="text-[11px] font-manrope font-semibold uppercase tracking-wide bg-[#EBE8E0] text-charcoal rounded px-2 py-0.5">{a.event_type||"event"}</span>
                            <div className="flex-1">
                                <div className="text-[13px] text-charcoal">{a.description||"—"}</div>
                                <div className="text-[11px] text-[#999]">{a.actor_id ? `by ${a.actor_id}` : ""} {a.workstage?`· WS ${a.workstage}`:""}</div>
                            </div>
                            <div className="text-[11px] text-[#999] whitespace-nowrap">{fmt(a.created_at)}</div>
                        </li>
                    ))}
                </ul>}
            </div>
        </div>
    );
}
